import type { CustomExperimentSpec } from '@learnforge/demo-render/harness';
import { defineExperiment } from '@learnforge/demo-render/harness';
import { createSvgEl, createZoomViewport } from '@learnforge/demo-render';
import { formatNumber } from '@learnforge/demo-math/numeric';
import { createToggleRow } from '@learnforge/demo-kit';
import { createAxes } from '../render/axes';
import { createDragHandle } from '../render/drag-handle';
import { createAngleArc } from '../render/arc';
import { ratiosFromPoint } from '../trig/ratios';
import { snapToSpecialRadian } from '../trig/angles';
import { createActionBar } from '../action-bar';
import type { TrigContext } from '../types';
import type { TrigKey } from '../i18n';

const BASE_VIEW = { x: -2, y: -1.6, width: 4, height: 3.2 };

// ---------------------------------------------------------------------------
// State — model only (no DOM refs)
// ---------------------------------------------------------------------------

interface State {
  visible: {
    sin: boolean;
    cos: boolean;
    tan: boolean;
    csc: boolean;
    sec: boolean;
    cot: boolean;
    astc: boolean;
  };
  units: 'deg' | 'rad' | 'both';
  snapCommon: boolean;
  theta: number;
  px: number;
  py: number;
}

// ---------------------------------------------------------------------------
// Spec factory
// ---------------------------------------------------------------------------

function buildSpec(): CustomExperimentSpec<State, TrigKey, TrigContext> {
  return {
    labelKey: 'experiment.unit-circle.label',
    demoId: 'trigonometry',
    advancedKey: 'unit-circle',

    initialState(): State {
      const theta = Math.PI / 4;
      return {
        visible: {
          sin: true,
          cos: true,
          tan: true,
          csc: false,
          sec: false,
          cot: false,
          astc: false,
        },
        units: 'deg',
        snapCommon: false,
        theta,
        px: Math.cos(theta),
        py: Math.sin(theta),
      };
    },

    createRender(ctx, host, controls) {
      // ---- SVG stage ----
      const svg = createSvgEl('svg', {
        viewBox: `${BASE_VIEW.x} ${BASE_VIEW.y} ${BASE_VIEW.width} ${BASE_VIEW.height}`,
        preserveAspectRatio: 'xMidYMid meet',
        class: 'tg-svg',
        'font-size': '0.08',
      });
      host.appendChild(svg);

      // Outer <g> flips Y so math coords point up
      const world = createSvgEl('g', { transform: 'scale(1 -1)' });
      svg.appendChild(world);

      // ---- Axes ----
      const axes = createAxes({
        xDomain: [-2, 2],
        yDomain: [-1.6, 1.6],
        i18n: ctx.i18n,
      });
      world.appendChild(axes.group);

      // ---- Unit circle ----
      const unitCircle = createSvgEl('circle', {
        class: 'tg-unit-circle',
        cx: 0,
        cy: 0,
        r: 1,
        'vector-effect': 'non-scaling-stroke',
      });
      world.appendChild(unitCircle);

      // ---- Segment group ----
      const segmentGroup = createSvgEl('g', { class: 'tg-segments' });
      world.appendChild(segmentGroup);

      // ---- ASTC overlay group (rendered above segments but below handle) ----
      const astcGroup = createSvgEl('g', { class: 'tg-astc' });
      world.appendChild(astcGroup);

      // ---- Angle arc ----
      const initialTheta = Math.PI / 4;
      const arc = createAngleArc(world, {
        cx: 0,
        cy: 0,
        r: 0.25,
        startAngle: 0,
        endAngle: initialTheta,
        showLabel: true,
        labelUnit: 'deg',
        i18n: ctx.i18n,
      });

      // ---- Drag handle ----
      const handleGroup = createSvgEl('g');
      world.appendChild(handleGroup);

      // Closure variable: always reflects the last update(state) call.
      // onMove/snap callbacks read this so they see live state without
      // mutating closure scalars.
      let current: State = {
        visible: {
          sin: true,
          cos: true,
          tan: true,
          csc: false,
          sec: false,
          cot: false,
          astc: false,
        },
        units: 'deg',
        snapCommon: false,
        theta: initialTheta,
        px: Math.cos(initialTheta),
        py: Math.sin(initialTheta),
      };

      const handle = createDragHandle(handleGroup, {
        cx: Math.cos(initialTheta),
        cy: Math.sin(initialTheta),
        r: 0.08,
        snap: (x, y) => {
          // Always project onto the unit circle
          const len = Math.hypot(x, y) || 1;
          const nx = x / len;
          const ny = y / len;
          if (!current.snapCommon) return { x: nx, y: ny };
          // Snap angle to nearest multiple of π/12
          const ang = Math.atan2(ny, nx);
          const step = Math.PI / 12;
          const snapped = Math.round(ang / step) * step;
          return { x: Math.cos(snapped), y: Math.sin(snapped) };
        },
        onMove: ({ x, y }) => {
          controls.setState((s) => ({ ...s, px: x, py: y, theta: Math.atan2(y, x) }));
        },
      });

      // ---- Clip bounds for tan/cot; tracked by the zoom viewport below. ----
      let clipX = 1.9;
      let clipY = 1.5;

      // ---- Zoom / pan. Clip bounds for tan/cot follow the visible domain. ----
      // Declared after clipX/clipY (creation fires onChange once, synchronously,
      // which assigns them — the `let`s must already be initialised by then).
      let sceneReady = false;
      const viewport = createZoomViewport(svg, {
        base: BASE_VIEW,
        yUp: true,
        fontSize: 0.08,
        onChange: (view) => {
          axes.update({ xDomain: view.xDomain, yDomain: view.yDomain, chromeScale: 1 / view.zoom });
          handle.setScale(1 / view.zoom);
          clipX = Math.max(-view.xDomain[0], view.xDomain[1]);
          clipY = Math.max(-view.yDomain[0], view.yDomain[1]);
          if (sceneReady) update(current);
        },
      });

      // ---- Panel + action bar ----
      const panelHandles = buildPanel();
      const actionBarHandle = buildActionBar();

      // ---------------------------------------------------------------------------
      // Segment geometry helpers
      // ---------------------------------------------------------------------------

      /**
       * Intersection of the ray from origin through (px, py) with the vertical
       * tangent line x = signX (where signX = ±1 based on quadrant).
       * Returns null when the point is at ±90° (asymptote).
       */
      function tanIntercept(px: number, py: number): { x: number; y: number } | null {
        if (Math.abs(px) < 1e-9) return null;
        // In Q1/Q4 use x=1; in Q2/Q3 use x=-1
        const tx = px > 0 ? 1 : -1;
        const ty = (py / px) * tx;
        if (Math.abs(ty) > clipY) return null; // clip at viewBox boundary
        return { x: tx, y: ty };
      }

      /**
       * Intersection of the ray from origin through (px, py) with the horizontal
       * tangent line y = signY.
       * Returns null when the point is on the x-axis (asymptote for cot).
       */
      function cotIntercept(px: number, py: number): { x: number; y: number } | null {
        if (Math.abs(py) < 1e-9) return null;
        // In Q1/Q2 use y=1; in Q3/Q4 use y=-1
        const ty = py > 0 ? 1 : -1;
        const tx = (px / py) * ty;
        if (Math.abs(tx) > clipX) return null;
        return { x: tx, y: ty };
      }

      function makeLine(
        cls: string,
        x1: number,
        y1: number,
        x2: number,
        y2: number,
      ): SVGLineElement {
        return createSvgEl('line', {
          class: cls,
          x1,
          y1,
          x2,
          y2,
          'vector-effect': 'non-scaling-stroke',
        });
      }

      // ---------------------------------------------------------------------------
      // Redraw ASTC overlay
      // ---------------------------------------------------------------------------

      function redrawAstc(visible: State['visible']): void {
        while (astcGroup.firstChild) astcGroup.removeChild(astcGroup.firstChild);
        if (!visible.astc) return;

        const labels: Array<{ x: number; y: number; text: string }> = [
          { x: 0.7, y: 0.7, text: 'All +' }, // Q1: upper-right
          { x: -0.7, y: 0.7, text: 'Sin +' }, // Q2: upper-left
          { x: -0.7, y: -0.7, text: 'Tan +' }, // Q3: lower-left
          { x: 0.7, y: -0.7, text: 'Cos +' }, // Q4: lower-right
        ];

        for (const lbl of labels) {
          const t = createSvgEl('text', {
            class: 'tg-astc-label',
            x: lbl.x,
            // y is in world-space (flipped), so pass as-is; CSS handles the flip-back
            y: lbl.y,
            'text-anchor': 'middle',
            'dominant-baseline': 'middle',
            transform: 'scale(1 -1)',
          });
          t.textContent = lbl.text;
          astcGroup.appendChild(t);
        }
      }

      // ---------------------------------------------------------------------------
      // Panel builder
      // ---------------------------------------------------------------------------

      interface PanelHandles {
        angleValueEl: HTMLSpanElement;
        sinValueEl: HTMLSpanElement;
        cosValueEl: HTMLSpanElement;
        tanValueEl: HTMLSpanElement;
        cscValueEl: HTMLSpanElement;
        secValueEl: HTMLSpanElement;
        cotValueEl: HTMLSpanElement;
        unitBtns: Array<{ btn: HTMLButtonElement; key: 'deg' | 'rad' | 'both' }>;
        valueElsMap: Map<string, { el: HTMLSpanElement; visKey: string }>;
      }

      function buildPanel(): PanelHandles {
        ctx.panel.replaceChildren();

        // --- Units picker (deg / rad / both) ---
        const unitsSection = document.createElement('div');
        unitsSection.className = 'tg-panel-section';

        const unitsLabel = document.createElement('div');
        unitsLabel.className = 'tg-panel-label';
        unitsLabel.textContent = ctx.i18n.t('uc.units');
        unitsSection.appendChild(unitsLabel);

        const unitsPicker = document.createElement('div');
        unitsPicker.className = 'tg-btn-group';

        const unitOptions: Array<{
          key: 'deg' | 'rad' | 'both';
          labelKey: 'uc.units.deg' | 'uc.units.rad' | 'uc.units.both';
        }> = [
          { key: 'deg', labelKey: 'uc.units.deg' },
          { key: 'rad', labelKey: 'uc.units.rad' },
          { key: 'both', labelKey: 'uc.units.both' },
        ];

        const unitBtns = unitOptions.map(({ key, labelKey }) => {
          const btn = document.createElement('button');
          btn.textContent = ctx.i18n.t(labelKey);
          btn.className = key === 'deg' ? 'tg-btn tg-btn-active' : 'tg-btn';
          btn.addEventListener('click', () => {
            controls.setState((s) => ({ ...s, units: key }));
          });
          unitsPicker.appendChild(btn);
          return { btn, key };
        });

        unitsSection.appendChild(unitsPicker);
        ctx.panel.appendChild(unitsSection);

        // --- Angle readout ---
        const angleRow = document.createElement('div');
        angleRow.className = 'tg-readout-row';
        const angleLabel = document.createElement('span');
        angleLabel.className = 'tg-panel-label';
        angleLabel.textContent = ctx.i18n.t('uc.angle.value');
        angleRow.appendChild(angleLabel);
        const angleValueEl = document.createElement('span');
        angleValueEl.className = 'tg-ratio-value';
        angleRow.appendChild(angleValueEl);
        ctx.panel.appendChild(angleRow);

        // --- Per-ratio reveal toggles + value readouts ---
        const ratioKeys: Array<{
          toggleKey:
            | 'uc.show.sin'
            | 'uc.show.cos'
            | 'uc.show.tan'
            | 'uc.show.csc'
            | 'uc.show.sec'
            | 'uc.show.cot';
          visKey: 'sin' | 'cos' | 'tan' | 'csc' | 'sec' | 'cot';
          defaultChecked: boolean;
        }> = [
          { toggleKey: 'uc.show.sin', visKey: 'sin', defaultChecked: true },
          { toggleKey: 'uc.show.cos', visKey: 'cos', defaultChecked: true },
          { toggleKey: 'uc.show.tan', visKey: 'tan', defaultChecked: true },
          { toggleKey: 'uc.show.csc', visKey: 'csc', defaultChecked: false },
          { toggleKey: 'uc.show.sec', visKey: 'sec', defaultChecked: false },
          { toggleKey: 'uc.show.cot', visKey: 'cot', defaultChecked: false },
        ];

        const valueElsMap = new Map<string, { el: HTMLSpanElement; visKey: string }>();

        for (const { toggleKey, visKey, defaultChecked } of ratioKeys) {
          const row = document.createElement('div');
          row.className = 'tg-readout-row';

          const valueEl = document.createElement('span');
          valueEl.className = 'tg-ratio-value';
          valueEl.style.display = defaultChecked ? '' : 'none';
          valueElsMap.set(visKey, { el: valueEl, visKey });

          const toggle = createToggleRow({
            labelKey: toggleKey,
            i18n: ctx.i18n,
            checked: defaultChecked,
            onChange: (v) => {
              controls.setState((s) => ({
                ...s,
                visible: { ...s.visible, [visKey]: v },
              }));
            },
          });
          row.appendChild(toggle.element);
          row.appendChild(valueEl);

          ctx.panel.appendChild(row);
        }

        // --- ASTC toggle ---
        const astcToggle = createToggleRow({
          labelKey: 'uc.show.astc',
          i18n: ctx.i18n,
          checked: false,
          onChange: (v) => {
            controls.setState((s) => ({
              ...s,
              visible: { ...s.visible, astc: v },
            }));
          },
        });
        ctx.panel.appendChild(astcToggle.element);

        // Safe extraction — all six keys are always set by the loop above
        const getEl = (k: string): HTMLSpanElement => {
          const entry = valueElsMap.get(k);
          if (!entry) throw new Error(`Missing value element for ratio "${k}"`);
          return entry.el;
        };

        return {
          angleValueEl,
          sinValueEl: getEl('sin'),
          cosValueEl: getEl('cos'),
          tanValueEl: getEl('tan'),
          cscValueEl: getEl('csc'),
          secValueEl: getEl('sec'),
          cotValueEl: getEl('cot'),
          unitBtns,
          valueElsMap,
        };
      }

      // ---------------------------------------------------------------------------
      // Action bar builder
      // ---------------------------------------------------------------------------

      function buildActionBar(): ReturnType<typeof createActionBar> {
        const actionBar = createActionBar(ctx.actionBar, {
          buttons: [
            {
              id: 'snap',
              labelKey: 'uc.snap.common',
              onClick: () => {
                controls.setState((s) => {
                  const nextSnap = !s.snapCommon;
                  if (nextSnap) {
                    // Force re-snap the current position
                    const step = Math.PI / 12;
                    const snappedTheta = Math.round(s.theta / step) * step;
                    return {
                      ...s,
                      snapCommon: nextSnap,
                      theta: snappedTheta,
                      px: Math.cos(snappedTheta),
                      py: Math.sin(snappedTheta),
                    };
                  }
                  return { ...s, snapCommon: nextSnap };
                });
              },
            },
            { id: 'view', labelKey: 'view.reset', onClick: () => viewport.reset() },
          ],
          i18n: ctx.i18n,
        });
        actionBar.setActive('snap', false);
        return actionBar;
      }

      // ---------------------------------------------------------------------------
      // Format helpers
      // ---------------------------------------------------------------------------

      function formatAngle(a: number, units: State['units']): string {
        // Normalise to [0, 2π)
        const norm = ((a % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
        const degStr = `${((norm * 180) / Math.PI).toFixed(1)}°`;
        const snap = snapToSpecialRadian(norm);
        let radStr: string;
        if (snap) {
          if (snap.num === 0) radStr = '0';
          else if (snap.num === snap.den) radStr = 'π';
          else if (snap.den === 1) radStr = `${snap.num}π`;
          else if (snap.num === 1) radStr = `π/${snap.den}`;
          else radStr = `${snap.num}π/${snap.den}`;
        } else {
          radStr = `${norm.toFixed(3)} rad`;
        }
        if (units === 'deg') return degStr;
        if (units === 'rad') return radStr;
        return `${degStr} = ${radStr}`;
      }

      // ---------------------------------------------------------------------------
      // update — reconcile view from state
      // ---------------------------------------------------------------------------

      function update(state: State): void {
        // First: refresh the live-state reference used by drag callbacks.
        current = state;

        const { visible, units, snapCommon, theta, px, py } = state;

        // Clear segments
        while (segmentGroup.firstChild) segmentGroup.removeChild(segmentGroup.firstChild);

        // sin: vertical drop from (px, py) to (px, 0)
        if (visible.sin) {
          segmentGroup.appendChild(makeLine('tg-segment-sin', px, py, px, 0));
        }

        // cos: horizontal from origin to (px, 0)
        if (visible.cos) {
          segmentGroup.appendChild(makeLine('tg-segment-cos', 0, 0, px, 0));
        }

        const ti = tanIntercept(px, py);
        const ci = cotIntercept(px, py);

        // tan: from tangent point (±1, 0) to tangent intercept
        if (visible.tan && ti !== null) {
          const tx = ti.x > 0 ? 1 : -1;
          segmentGroup.appendChild(makeLine('tg-segment-tan', tx, 0, ti.x, ti.y));
        }

        // cot: from (0, ±1) to cot intercept
        if (visible.cot && ci !== null) {
          const ty = ci.y > 0 ? 1 : -1;
          segmentGroup.appendChild(makeLine('tg-segment-cot', 0, ty, ci.x, ci.y));
        }

        // sec: from origin to the tan-intercept
        if (visible.sec && ti !== null) {
          segmentGroup.appendChild(makeLine('tg-segment-sec', 0, 0, ti.x, ti.y));
        }

        // csc: from origin to the cot-intercept
        if (visible.csc && ci !== null) {
          segmentGroup.appendChild(makeLine('tg-segment-csc', 0, 0, ci.x, ci.y));
        }

        // ASTC overlay
        redrawAstc(visible);

        // Arc + handle position
        arc.update({ endAngle: theta, labelUnit: units });
        handle.setPosition(px, py);

        // Update panel — unit buttons active state
        for (const { btn, key } of panelHandles.unitBtns) {
          btn.className = key === units ? 'tg-btn tg-btn-active' : 'tg-btn';
        }

        // Update action bar snap active state
        actionBarHandle.setActive('snap', snapCommon);

        // Update ratio toggle value readouts + visibility
        for (const [visKey, { el }] of panelHandles.valueElsMap) {
          const isVisible = visible[visKey as keyof typeof visible];
          el.style.display = isVisible ? '' : 'none';
        }

        // Update panel numeric readouts
        const r = ratiosFromPoint(px, py);
        panelHandles.sinValueEl.textContent = formatNumber(r.sin, { decimals: 4, nonFinite: '∞' });
        panelHandles.cosValueEl.textContent = formatNumber(r.cos, { decimals: 4, nonFinite: '∞' });
        panelHandles.tanValueEl.textContent = formatNumber(r.tan, { decimals: 4, nonFinite: '∞' });
        panelHandles.cscValueEl.textContent = formatNumber(r.csc, { decimals: 4, nonFinite: '∞' });
        panelHandles.secValueEl.textContent = formatNumber(r.sec, { decimals: 4, nonFinite: '∞' });
        panelHandles.cotValueEl.textContent = formatNumber(r.cot, { decimals: 4, nonFinite: '∞' });
        panelHandles.angleValueEl.textContent = formatAngle(theta, units);
      }

      // Initial render
      sceneReady = true;
      update(current);

      return {
        update,
        dispose() {
          handle.destroy();
          arc.destroy();
          axes.destroy();
          actionBarHandle.destroy();
          viewport.dispose();
        },
      };
    },

    applyShotState(state): State {
      return state;
    },
  };
}

export const experiment = defineExperiment(buildSpec());

// Alias for the existing index.ts + test imports (behaviorally identical).
export const mountUnitCircle = experiment.mount.bind(experiment);
